import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Save, RefreshCw, Users, DollarSign, TrendingUp, Server, Briefcase, Calculator, Bot } from 'lucide-react';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';
import { listPremises, upsertPremise, batchUpsertPremises, generateProjection } from '../../services/financialPlanningService';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD = '#b48c2c';

const CATEGORY_META = {
  clients: { label: 'Clientes', icon: Users, color: '#6366f1' },
  pricing: { label: 'Precos & Planos', icon: DollarSign, color: '#16a34a' },
  costs: { label: 'Custos Variaveis (por cliente)', icon: Server, color: '#dc2626' },
  fixed_costs: { label: 'Custos Fixos', icon: Briefcase, color: '#f59e0b' },
  headcount: { label: 'Headcount', icon: Users, color: '#8b5cf6' },
  marketing: { label: 'Marketing & Vendas', icon: TrendingUp, color: '#ec4899' },
  investment: { label: 'Investimento (Captacao)', icon: DollarSign, color: '#0ea5e9' },
  tax: { label: 'Impostos', icon: Calculator, color: '#64748b' },
  growth: { label: 'Crescimento', icon: TrendingUp, color: '#059669' },
  cash: { label: 'Caixa & Investimentos', icon: DollarSign, color: BRAND_GOLD },
};

const formatValue = (value, unit, lang) => {
  if (value == null) return '—';
  if (unit === 'BRL') return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  if (unit === 'USD') return `$ ${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  if (unit === '%') return `${value}%`;
  if (unit === 'qty') return `${Math.round(value)}`;
  if (unit === 'months') return lang === 'en' ? `${value} months` : `${value} meses`;
  return String(value);
};

const PremiseRow = ({ premise, onSave, lang }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(premise.value_number ?? '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave({ ...premise, value_number: parseFloat(value) || 0 });
    setSaving(false);
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') { setEditing(false); setValue(premise.value_number ?? ''); }
  };

  return (
    <tr className="border-b last:border-0 hover:bg-gray-50 group">
      <td className="px-4 py-2.5">
        <div className="text-sm font-medium text-gray-900">{premise.label}</div>
        {premise.notes && <div className="text-xs text-gray-400">{premise.notes}</div>}
      </td>
      <td className="px-4 py-2.5 text-right">
        {editing ? (
          <div className="flex items-center gap-1 justify-end">
            <input
              type="number"
              step="any"
              value={value}
              onChange={e => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              className="w-32 px-2 py-1 border rounded text-sm text-right font-mono"
            />
            <button onClick={handleSave} disabled={saving}
              className="p-1 rounded bg-green-100 text-green-700 hover:bg-green-200">
              <Save size={14} />
            </button>
          </div>
        ) : (
          <span
            onClick={() => setEditing(true)}
            className="cursor-pointer font-mono text-sm font-semibold text-gray-900 hover:text-blue-600 px-2 py-1 rounded hover:bg-blue-50 transition"
          >
            {formatValue(premise.value_number, premise.unit, lang)}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 text-center">
        <span className="text-xs text-gray-400">{premise.unit}</span>
      </td>
    </tr>
  );
};

const FinancialPremisesTab = () => {
  const { lang } = useGovernanceLang();
  const L = {
    pt: {
      title: 'Premissas do Modelo',
      subtitle: 'Altere qualquer valor e gere a projeção atualizada',
      aiBtn: 'Preencher Modelo (IA)', aiBtnBusy: 'Preenchendo...',
      generateBtn: 'Gerar Projeção', generateBtnBusy: 'Gerando...',
      optimistic: 'Otimista', pessimistic: 'Pessimista',
      alertOk: (n) => `Projeção gerada: ${n} lançamentos criados para`,
      alertMonths: (m) => `${m} meses.`,
      confirmAI: 'A IA vai preencher todas as premissas com valores sugeridos para uma startup SaaS B2B sport-tech. Você poderá editar depois. Continuar?',
      alertAIErr: 'Erro ao preencher modelo',
      alertProjErr: 'Erro ao gerar projeção',
    },
    en: {
      title: 'Model Assumptions',
      subtitle: 'Edit any value and regenerate the projection',
      aiBtn: 'Fill Model (AI)', aiBtnBusy: 'Filling...',
      generateBtn: 'Generate Projection', generateBtnBusy: 'Generating...',
      optimistic: 'Optimistic', pessimistic: 'Pessimistic',
      alertOk: (n) => `Projection generated: ${n} entries created for`,
      alertMonths: (m) => `${m} months.`,
      confirmAI: 'AI will fill all assumptions with suggested values for a B2B sport-tech SaaS startup. You can edit them afterwards. Continue?',
      alertAIErr: 'Error filling model',
      alertProjErr: 'Error generating projection',
    },
  }[lang] || {};
  const [premises, setPremises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filling, setFilling] = useState(false);
  const [scenario, setScenario] = useState('base');

  const loadPremises = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listPremises(null, scenario);
      setPremises(res?.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [scenario]);

  useEffect(() => { loadPremises(); }, [loadPremises]);

  const handleSave = async (premise) => {
    try {
      await upsertPremise(premise);
      await loadPremises();
    } catch (e) { console.error(e); }
  };

  const handleAIFill = async () => {
    if (!window.confirm(L.confirmAI)) return;
    setFilling(true);
    try {
      const { AZURE } = await import('../../services/api');
      const res = await AZURE.post('/governance/finance/planning/premises/ai-fill', { scenario });
      if (res.data?.success) {
        await loadPremises();
        alert(`${L.alertOk(res.data?.data?.updated || 0)} ${L.alertMonths(res.data?.data?.months_projected || 12)}`);
      }
    } catch (e) { console.error(e); alert(L.alertAIErr); }
    setFilling(false);
  };

  const handleGenerateProjection = async () => {
    setGenerating(true);
    try {
      const res = await generateProjection(12, scenario);
      alert(`${L.alertOk(res?.data?.entries_created || 0)} ${L.alertMonths(res?.data?.months_projected || 12)}`);
    } catch (e) { console.error(e); alert(L.alertProjErr); }
    setGenerating(false);
  };

  // Group by category
  const grouped = {};
  premises.forEach(p => {
    if (!grouped[p.category]) grouped[p.category] = [];
    grouped[p.category].push(p);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings size={20} style={{ color: BRAND_GREEN }} />
          <div>
            <h2 className="text-lg font-bold text-gray-900">{L.title}</h2>
            <p className="text-xs text-gray-500">{L.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select value={scenario} onChange={e => setScenario(e.target.value)}
            className="text-sm border rounded-lg px-3 py-1.5">
            <option value="base">Base</option>
            <option value="optimistic">{L.optimistic}</option>
            <option value="pessimistic">{L.pessimistic}</option>
          </select>
          <button onClick={handleAIFill} disabled={filling}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm border-2"
            style={{ borderColor: BRAND_GOLD, color: BRAND_GOLD }}>
            <Bot size={14} className={filling ? 'animate-pulse' : ''} />
            {filling ? L.aiBtnBusy : L.aiBtn}
          </button>
          <button onClick={handleGenerateProjection} disabled={generating}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-bold text-sm"
            style={{ backgroundColor: BRAND_GREEN }}>
            <RefreshCw size={14} className={generating ? 'animate-spin' : ''} />
            {generating ? L.generateBtnBusy : L.generateBtn}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1,2,3].map(i => <div key={i} className="h-40 bg-white rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.entries(CATEGORY_META).map(([catKey, meta]) => {
            const items = grouped[catKey] || [];
            if (items.length === 0) return null;
            const Icon = meta.icon;
            return (
              <div key={catKey} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
                  <Icon size={16} style={{ color: meta.color }} />
                  <h3 className="font-bold text-sm text-gray-800">{meta.label}</h3>
                  <span className="text-xs text-gray-400 ml-auto">{items.length} itens</span>
                </div>
                <table className="w-full">
                  <tbody>
                    {items.map(p => (
                      <PremiseRow key={p.id} premise={p} onSave={handleSave} lang={lang} />
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FinancialPremisesTab;
